import React, { useState, useEffect, useRef } from 'react';
import useTranslation from '../i18n/useTranslation';
import '../styles/ai-player.css';

const MessagePanel = ({ messages, onSendMessage, players, currentPlayerId }) => {
  const { t } = useTranslation();
  const [messageText, setMessageText] = useState('');
  const [recipient, setRecipient] = useState('public');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = () => {
    if (messageText.trim() === '') {
      return;
    }

    onSendMessage(recipient, messageText.trim());
    setMessageText('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getRecipientName = (toId) => {
    if (toId === 'public') return t.messages.everyone;
    const player = players.find(p => p.id === toId);
    return player ? player.name : t.messages.unknown;
  };

  return (
    <div className="message-panel">
      <div className="message-header">
        <span className="message-icon">💬</span>
        <span>{t.messages.title}</span>
      </div>

      <div className="message-list">
        {messages.length === 0 ? (
          <div className="no-messages">{t.messages.noMessages}</div>
        ) : (
          messages
            .filter(msg => {
              // Filter out "****" private message notifications for sender and receiver
              if (msg.text === '****' && msg.to !== 'public') {
                return msg.from !== currentPlayerId && msg.to !== currentPlayerId;
              }
              return true;
            })
            .map((msg, idx) => (
              <div
                key={idx}
                className={`message-item ${msg.isAI ? 'ai-message' : 'human-message'} ${
                  msg.from === currentPlayerId ? 'own-message' : ''
                }`}
              >
                <div className="message-header-line">
                  <span className="message-sender">
                    {msg.isAI && <span className="robot-icon">🤖</span>}
                    {msg.fromName || msg.from}
                  </span>
                  {msg.to !== 'public' && (
                    <span className="message-recipient">
                      → {getRecipientName(msg.to)}
                    </span>
                  )}
                  <span className="message-timestamp">
                    {formatTimestamp(msg.timestamp)}
                  </span>
                </div>
                <div className="message-text">{msg.text}</div>
              </div>
            ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="message-input-container">
        <select
          className="message-recipient-select"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
        >
          <option value="public">{t.messages.publicRecipient}</option>
          {players
            .filter(p => p.id !== currentPlayerId)
            .map(p => (
              <option key={p.id} value={p.id}>
                {p.isAI ? '🤖 ' : '👤 '}{p.name}
              </option>
            ))}
        </select>
        <input
          type="text"
          className="message-input"
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={t.messages.placeholder}
          maxLength={200}
        />
        <button
          className="message-send-button"
          onClick={handleSend}
          disabled={messageText.trim() === ''}
        >
          {t.messages.send}
        </button>
      </div>
    </div>
  );
};

export default MessagePanel;
